import { useGetActivePortals } from '@/api'
import { ROUTER_PATHS } from '@/lib/constants'
import { Center, CircularProgress, ComposedAlert, PageTabs, TabList } from '@eigtech/ui-shared-dave'
import { LinkTab } from '@eigtech/ui-shared-router'
import { Navigate, createFileRoute, useMatches } from '@tanstack/react-router'
import { useMemo } from 'react'

export const Route = createFileRoute('/_auth/')({
  component: Homepage,
})

function Homepage() {
  const { hasClaims, hasJobs, isPending, isError } = useHasPortals()

  if (isError) {
    return (
      <Center my="16">
        <ComposedAlert
          alert={{
            title: 'Whoops!',
            description:
              'Something went wrong. Please wait and try again. If the issue persists, contact EIG Tech Support.',
          }}
          maxW="container.text"
          status="warning"
        />
      </Center>
    )
  }

  if (isPending) {
    return (
      <Center my="16">
        <CircularProgress isIndeterminate size={100} />
      </Center>
    )
  }

  if (!(hasClaims || hasJobs)) {
    return (
      <Center my="16">
        <ComposedAlert
          alert={{
            title: 'No assignments found for you',
            description:
              'Please wait and try again. If the issue persists, contact EIG Tech Support.',
          }}
          maxW="container.text"
          status="warning"
        />
      </Center>
    )
  }

  if (hasJobs && !hasClaims) {
    return <Navigate replace to={ROUTER_PATHS.jobs.route} />
  }

  return <Navigate replace to={ROUTER_PATHS.claims.route} />
}

export function HomepageToolbar() {
  const { hasClaims, hasJobs } = useHasPortals()

  const matches = useMatches()

  const routes = useMemo(
    () => [
      ...(hasClaims
        ? [
            {
              url: { to: ROUTER_PATHS.claims.route },
              id: ROUTER_PATHS.claims.routeId,
              label: 'Claims',
            },
          ]
        : []),
      ...(hasJobs
        ? [
            {
              url: { to: ROUTER_PATHS.jobs.route },
              id: ROUTER_PATHS.jobs.routeId,
              label: 'Jobs',
            },
          ]
        : []),
    ],
    [hasClaims, hasJobs]
  )

  const tabIndex =
    routes.findIndex((route) => matches.some((match) => match.routeId === route.id)) ?? 0

  if (routes.length < 2) return null

  return (
    <PageTabs index={tabIndex} variant="soft-rounded">
      <TabList>
        {routes.map((tab) => (
          <LinkTab key={tab.id} data-id={tab.id} position="relative" {...tab.url}>
            {tab.label}
          </LinkTab>
        ))}
      </TabList>
    </PageTabs>
  )
}

function useHasPortals() {
  const { data: portals, isPending, isError } = useGetActivePortals()

  const hasClaims = useMemo(
    () => portals?.some((portal) => portal.entityType === 'claim'),
    [portals]
  )
  const hasJobs = useMemo(() => portals?.some((portal) => portal.entityType === 'job'), [portals])

  return { hasClaims, hasJobs, portals, isPending, isError }
}
